import React, { useEffect, useState } from "react";
import { getMerchant } from '../../helpers/ReduxHelpers';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants';
import { useForm } from "react-hook-form";
import { Box, Button, FormControlLabel, Grid, Switch, Tooltip, Typography } from "@mui/material";
import { FormProvider, RHFTextField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { primaryBtnSx, secondaryBtnSx, filterBarSx } from '../../styles/patterns';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE } from '../../styles/foundation/colors';
import { primaryTextSx, cellStackSx } from '../orders/list2/cellStyles';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup'
import Loader from "../../common/Loader"
import { useSnackbar } from "notistack";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import ConfirmDialog from "../../common/ConfirmDialog";

const LostAndDamageEmail = () => {
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [weightDiscrepancyConfig, setWDConfig] = useState();
    const [isLNDEnabled, setLNDEnabled] = useState(false);
    const [reload, setReload] = useState(0);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleteData, setDeleteData] = useState();
    const [switchConfirm, setSwitchConfirm] = useState(false);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);


    const columns = [
        {
            field: 'emails',
            headerAlign: "left",
            align: 'left',
            hideSortIcons: true,
            headerName: 'Emails',
            flex: 1.2,
            minWidth: 250,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}><Typography sx={primaryTextSx}>{row?.email}</Typography></Box>
                )
            },
        },
        {
            field: 'action',
            headerAlign: "left",
            align: 'left',
            hideSortIcons: true,
            headerName: 'Action',
            flex: 0.5,
            minWidth: 120,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Button
                        onClick={() => handleCancelOrder(row)}
                        disabled={!isLNDEnabled}
                        color="error"
                        variant="outlined"
                        size="small"
                    >
                        Delete
                    </Button>
                );
            },
        },
    ];

    const emailListRegExp = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}\s*,\s*)*[\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}$/

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                email: Yup.string()
                    .matches(emailListRegExp, { message: 'Invalid email format', excludeEmptyString: true })
                    .required("Email is required!"),
            })
        ),
        defaultValues: {
            email: '',
        }
    })
    const { handleSubmit: handleSubmitForm, reset } = methods

    const postMerchantConfig = (payload) => {
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}/lndConfig`, payload).then(response => {
            enqueueSnackbar('L&D email configuration updated successfully', { variant: 'success' })
            reset()
            setReload(reload => reload + 1)
            setLoading(false)
        })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                
                // Handle specific error cases
                if (err.response?.status === 400) {
                    if (err.response?.data?.message?.includes('emails') && err.response?.data?.message?.includes('required')) {
                        enqueueSnackbar('Please add at least one email address before enabling L&D emails', { variant: 'error' });
                    } else {
                        enqueueSnackbar(err.response?.data?.message || 'Invalid request. Please check your configuration.', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('Failed to update L&D email configuration. Please try again.', { variant: 'error' });
                }
            })
    }


    const handleCancelOrder = (data) => {
        setDeleteData(data)
        setDeleteConfirm(true)
    }
    const handleDelete = () => {
        const index = weightDiscrepancyConfig?.lostDamageConfig?.emails.indexOf(deleteData.email);
        if (index !== -1) {
            weightDiscrepancyConfig?.lostDamageConfig?.emails.splice(index, 1);
        }
        const payload = {
            "enableWA": false,
            "enableSMS": false,
            "mobileNos": [],
            "enableEmail": true,
            "emails": [...weightDiscrepancyConfig?.lostDamageConfig?.emails]
        }
        postMerchantConfig(payload)
    }


    const onSubmit = (data) => {
        setLoading(true);
        const existingEmails = weightDiscrepancyConfig?.lostDamageConfig?.emails ?? [];
        const emailsArray = data.email.split(',').map(email => email.trim());
        const payload = {
            "enableWA": false,
            "enableSMS": false,
            "mobileNos": [],
            "enableEmail": true,
            "emails": [...existingEmails, ...emailsArray]
        }
        postMerchantConfig(payload)
    }

    useEffect(() => {
        setLoading(true)
        getGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}`).then((res) => {
            setWDConfig(res.data);
            setLoading(false)
        }).catch((err) => {
            console.log(err)
            setLoading(false)
        })
    }, [merchantId, reload])


    const toggleEmail = (e) => {
        setLoading(true);
        let payload = {
            "enableWA": false,
            "enableSMS": false,
            "mobileNos": [],
            "enableEmail": !weightDiscrepancyConfig?.lostDamageConfig?.enableEmail,
            "emails": weightDiscrepancyConfig?.lostDamageConfig?.emails
        }
        
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}/lndConfig`, payload).then(response => {
            enqueueSnackbar('L&D email toggle updated successfully', { variant: 'success' })
            setReload(reload => reload + 1)
            setLoading(false)
        })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                
                // Handle specific error cases
                if (err.response?.status === 400) {
                    if (err.response?.data?.message?.includes('emails') && err.response?.data?.message?.includes('required')) {
                        enqueueSnackbar('Please add at least one email address before enabling L&D emails', { variant: 'error' });
                    } else {
                        enqueueSnackbar(err.response?.data?.message || 'Invalid request. Please check your configuration.', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('Failed to update L&D email toggle. Please try again.', { variant: 'error' });
                }
            })
    }
    useEffect(() => {
        setLNDEnabled(weightDiscrepancyConfig?.lostDamageConfig?.enableEmail)
    }, [weightDiscrepancyConfig?.lostDamageConfig?.enableEmail])

    const handleSwitchConfirmation = (e) => {
        // Check if trying to enable email but no emails exist
        if (!weightDiscrepancyConfig?.lostDamageConfig?.enableEmail && (!weightDiscrepancyConfig?.lostDamageConfig?.emails || weightDiscrepancyConfig?.lostDamageConfig?.emails.length === 0)) {
            enqueueSnackbar('Please add at least one email address before enabling L&D emails', { variant: 'error' });
            return;
        }
        setSwitchConfirm(true);
    };
    const handleSwitchAction = () => {
        toggleEmail()
    };
    return (
        <>
            {(isLoading) && <Loader />}
            <ConfirmDialog
                title=''
                open={deleteConfirm}
                setOpen={setDeleteConfirm}
                disableButtons={weightDiscrepancyConfig?.lostDamageConfig?.emails?.length === 1}
                onConfirm={() => handleDelete()}
            >
                {weightDiscrepancyConfig?.lostDamageConfig?.emails?.length === 1 ? "Please disable via Toggle Switch" : "Are you sure you want to delete ?"}
            </ConfirmDialog>
            <ConfirmDialog
                title=''
                open={switchConfirm}
                setOpen={setSwitchConfirm}
                onConfirm={() => handleSwitchAction()}
            >
                Are you sure you want to change the email configuration?
            </ConfirmDialog>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <Box sx={filterBarSx}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY }}>Lost & Damage Email Configuration</Typography>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.5 }}>Configure email notifications for Lost & Damage</Typography>
                            </Box>
                            <Tooltip title={isLNDEnabled ? "Disable L&D Emails" : "Enable L&D Emails"}>
                                <FormControlLabel
                                    control={<Switch checked={isLNDEnabled} onChange={(e) => handleSwitchConfirmation(e)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }} />}
                                    label={<Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: isLNDEnabled ? '#4caf50' : SUBTLE }}>{isLNDEnabled ? 'Enabled' : 'Disabled'}</Typography>}
                                    labelPlacement="start"
                                />
                            </Tooltip>
                        </Box>
                        <FormProvider methods={methods} onSubmit={handleSubmitForm(onSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <RHFTextField
                                        name="email"
                                        label="Add Emails *"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LoadingButton loading={isLoading}
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        sx={primaryBtnSx}
                                    >Add</LoadingButton>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        sx={{ ml: 1, ...secondaryBtnSx }}
                                        onClick={() => reset()}
                                    >Reset</Button>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Box>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <ProzoDataGrid
                            columns={columns}
                            autoHeight={true}
                            rows={(weightDiscrepancyConfig && weightDiscrepancyConfig?.lostDamageConfig?.emails?.length > 0)
                                ? weightDiscrepancyConfig?.lostDamageConfig?.emails.map((email, index) => ({ id: index, email }))
                                : []
                            }
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            paginationMode='client'
                            pagination={true}
                            rowCount={weightDiscrepancyConfig?.lostDamageConfig?.emails?.length || 0}
                            // hideCountNumber={true}
                            getRowId={(row) => row.id}
                        />
                    </Grid>
                </Grid>
            </Grid >
        </>
    );
};

export default LostAndDamageEmail;
